import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, CircleStop, Play, Plus, Table2, X } from 'lucide-react';
import clsx from 'clsx';
import { ROW_LIMIT_OPTIONS, type SavedQuery, type SessionUser } from '@idas/shared';
import { api, ApiError } from '../lib/api';
import { useConsoleStore } from '../store/console.store';
import { SchemaTree } from '../components/SchemaTree';
import { SqlEditor, type SqlEditorHandle } from '../components/SqlEditor';
import { ResultPanel } from '../components/ResultPanel';
import { SplitPane } from '../components/SplitPane';
import { ExportDialog } from '../components/ExportDialog';
import { SavedQueriesPanel, SavedQueryModal } from '../components/SavedQueriesPanel';
import { toast } from '../components/ui/Toast';

/** How long the analyst must pause before the server re-checks their SQL. */
const VALIDATE_DEBOUNCE_MS = 450;

export function ConsolePage({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient();
  const editorRef = useRef<SqlEditorHandle>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'schema' | 'saved'>('schema');

  const {
    tabs,
    activeTabId,
    dataSourceId,
    theme,
    setDataSource,
    addTab,
    closeTab,
    setActiveTab,
    updateTab,
    rowLimit,
    setRowLimit,
    setDatabase,
  } = useConsoleStore();

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0]!;

  const datasources = useQuery({
    queryKey: ['datasources'],
    queryFn: api.datasources.list,
  });

  // Pick a connection automatically on first load so the console is usable
  // immediately rather than presenting an empty state that asks for a choice
  // most people only have one answer to.
  //
  // The remembered choice is also checked against what still exists. A
  // connection that has been deleted — or that this person's access to has
  // been withdrawn — leaves a perfectly plausible id in localStorage, and
  // treating "something is selected" as "a valid thing is selected" stranded
  // the console: the picker matched no option, the schema tree came back empty
  // for an id nobody could see, and nothing said why.
  useEffect(() => {
    const available = datasources.data?.datasources;
    if (!available?.length) return;
    if (dataSourceId && available.some((source) => source.id === dataSourceId)) return;
    setDataSource(available[0]!.id);
  }, [dataSourceId, datasources.data, setDataSource]);

  const catalog = useQuery({
    queryKey: ['catalog', dataSourceId],
    queryFn: () => api.catalog.snapshot(dataSourceId!),
    enabled: Boolean(dataSourceId),
  });

  /**
   * Choose the active database when the answer is obvious.
   *
   * With a single database there is nothing to decide, and making someone
   * click it before their first query would be ceremony. With several, the
   * connection's own configured database is the sensible opening position;
   * failing that the console leaves it unset and says so rather than guessing,
   * because guessing wrong silently changes what `FROM user` means.
   */
  useEffect(() => {
    const databases = catalog.data?.databases;
    if (!databases?.length) return;

    // A remembered database is only kept if it is still one of this
    // connection's. Repointing a connection at a different schema leaves every
    // open tab holding a name that no longer exists — the toolbar shows it,
    // and the server rejects the query as "not a database on this connection".
    // Re-picking is the honest repair; leaving it is a dead end the reader
    // cannot diagnose.
    if (activeTab.database && databases.some((d) => d.name === activeTab.database)) return;

    const configured = datasources.data?.datasources.find((d) => d.id === dataSourceId)?.database;
    const pick =
      databases.length === 1
        ? databases[0]!.name
        : databases.find((d) => d.name === configured)?.name;

    if (pick) setDatabase(pick);
  }, [catalog.data, activeTab.database, activeTab.id, dataSourceId, datasources.data, setDatabase]);

  const completions = useQuery({
    queryKey: ['completions', dataSourceId],
    queryFn: () => api.catalog.completions(dataSourceId!),
    enabled: Boolean(dataSourceId),
  });

  const refresh = useMutation({
    mutationFn: () => api.catalog.refresh(dataSourceId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalog', dataSourceId] });
      void queryClient.invalidateQueries({ queryKey: ['completions', dataSourceId] });
    },
  });

  // ── Live validation ──────────────────────────────────────────────────────
  //
  // Debounced, and every in-flight request is aborted when the next keystroke
  // lands. Without the abort, slow responses arrive out of order and the
  // editor ends up showing markers for SQL the analyst has already rewritten.
  const validateAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!dataSourceId) return;

    const sql = activeTab.sql;
    if (!sql.trim()) {
      updateTab(activeTab.id, { validation: null });
      return;
    }

    const timer = setTimeout(() => {
      validateAbort.current?.abort();
      const controller = new AbortController();
      validateAbort.current = controller;

      api.query
        .validate(
          { dataSourceId, sql, database: activeTab.database ?? undefined },
          controller.signal,
        )
        .then((validation) => updateTab(activeTab.id, { validation }))
        .catch((error) => {
          // An abort is the expected outcome of typing, not a failure.
          if (error instanceof DOMException && error.name === 'AbortError') return;
        });
    }, VALIDATE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [activeTab.sql, activeTab.id, activeTab.database, dataSourceId, updateTab]);

  // ── Execution ────────────────────────────────────────────────────────────
  const run = useCallback(async () => {
    const store = useConsoleStore.getState();
    const tab = store.tabs.find((t) => t.id === store.activeTabId);
    if (!tab || !store.dataSourceId || tab.running) return;

    const sql = editorRef.current?.getSelectedOrAll() ?? tab.sql;
    if (!sql.trim()) return;

    // Refused here rather than sent and rejected: without a database an
    // unqualified name has no meaning, and the honest answer is to say so
    // before the query goes anywhere.
    if (!tab.database) {
      store.updateTab(tab.id, {
        error:
          'No database selected. Pick one in the sidebar — it is what unqualified table ' +
          'names like "user" resolve against.',
      });
      return;
    }

    const clientQueryId = crypto.randomUUID();
    // Recorded before the request leaves, so Cancel has something to name
    // while the query is still running.
    store.updateTab(tab.id, {
      running: true,
      error: null,
      result: null,
      runId: null,
      clientQueryId,
    });

    try {
      const result = await api.query.execute({
        dataSourceId: store.dataSourceId,
        sql,
        clientQueryId,
        maxRows: store.rowLimit,
        database: tab.database,
      });
      store.updateTab(tab.id, { result, running: false, runId: result.queryRunId });
    } catch (error) {
      store.updateTab(tab.id, {
        running: false,
        error: error instanceof ApiError ? error.message : 'The query could not be sent.',
      });
    }
  }, []);

  /**
   * "Preview this table" from the schema tree.
   *
   * Opens its own tab rather than replacing what is in the editor — losing
   * someone's half-written query to a stray click would be unforgivable — and
   * runs immediately, since that is the whole point of the button.
   *
   * A LIMIT is written in explicitly rather than left to the server's cap, so
   * the analyst can see and change it.
   */
  const quickRun = useCallback(
    (database: string, table: string) => {
      const store = useConsoleStore.getState();
      const current = store.tabs.find((t) => t.id === store.activeTabId);
      // Qualified only when it has to be. Previewing a table in the active
      // database should produce the query an analyst would have written.
      const reference = current?.database === database ? table : `${database}.${table}`;
      const sql = `SELECT *\nFROM ${reference}\nLIMIT 100;`;
      const tabId = store.addTab(sql);
      store.renameTab(tabId, table);
      // Defer so the new tab is the active one before run() reads the store.
      setTimeout(() => void run(), 0);
    },
    // `run` reads the store fresh on each call, so it never goes stale.
    [],
  );

  /** Opens a saved query in its own tab, optionally running it immediately. */
  const openSaved = useCallback((query: SavedQuery, andRun: boolean) => {
    const store = useConsoleStore.getState();
    const tabId = store.addTab(query.sql);
    store.renameTab(tabId, query.name);
    if (andRun) setTimeout(() => void run(), 0);
  }, []);

  const cancel = useMutation({
    mutationFn: () => api.query.cancel(activeTab.clientQueryId ?? ''),
    // No toast on the way out. The server may kill the query now, or moments
    // later if it had not reached the engine yet — and the result panel
    // reports which happened within a second either way. Announcing an
    // outcome we do not yet know would just be wrong half the time.
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not cancel the query.'),
  });

  const selected = datasources.data?.datasources.find((d) => d.id === dataSourceId);
  const blocked = activeTab.validation?.valid === false;
  const canExport = user.permissions.includes('export:csv');

  return (
    <div className="flex h-full flex-col">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-edge bg-surface-raised px-2">
        <select
          className="input w-auto max-w-56 py-1"
          value={dataSourceId ?? ''}
          onChange={(event) => setDataSource(event.target.value)}
        >
          {datasources.data?.datasources.map((source) => (
            <option key={source.id} value={source.id}>
              {source.name} · {source.engine.toLowerCase()}
            </option>
          ))}
          {!datasources.data?.datasources.length && <option value="">No connections</option>}
        </select>

        {/* Which database unqualified names resolve against. Shown next to the
            connection because it changes what the same SQL means, and an
            analyst should never have to guess it from the sidebar. */}
        <select
          className={clsx(
            'input w-auto max-w-48 py-1',
            !activeTab.database && 'border-warn/50 text-warn',
          )}
          value={activeTab.database ?? ''}
          onChange={(event) => setDatabase(event.target.value || null)}
          title="The database unqualified table names resolve against"
        >
          {!activeTab.database && <option value="">No database selected</option>}
          {catalog.data?.databases.map((database) => (
            <option key={database.name} value={database.name}>
              {database.name}
            </option>
          ))}
        </select>

        <button
          className="btn btn-primary"
          onClick={() => void run()}
          disabled={activeTab.running || !dataSourceId || blocked}
          title={
            blocked
              ? 'This query is blocked by policy — see the underlined text.'
              : 'Run (Ctrl+Enter). Select text to run only that part.'
          }
        >
          <Play className="h-3 w-3 fill-current" />
          Run
        </button>

        {activeTab.running && (
          <button className="btn" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            <CircleStop className="h-3.5 w-3.5" />
            Cancel
          </button>
        )}

        <button
          className="btn"
          onClick={() => setSaving(true)}
          disabled={!activeTab.sql.trim() || !dataSourceId}
          title="Save this query so you can run it again later"
        >
          <Bookmark className="h-3.5 w-3.5" />
          Save
        </button>

        <div className="flex-1" />

        {/* Row cap, chosen per query the way phpMyAdmin does it. Smaller is
            faster — most of a large result's cost is transferring rows nobody
            scrolls to. */}
        <label className="flex items-center gap-1.5 text-2xs text-ink-faint">
          Rows
          <select
            className="input w-auto py-1"
            value={rowLimit}
            onChange={(event) => setRowLimit(Number(event.target.value))}
          >
            {ROW_LIMIT_OPTIONS.map((option) => {
              const overCap = selected ? option > selected.limits.maxRows : false;
              return (
                <option key={option} value={option} disabled={overCap}>
                  {option.toLocaleString()}
                  {overCap ? ' — above cap' : ''}
                </option>
              );
            })}
          </select>
        </label>

        {selected && (
          <span
            className="text-2xs text-ink-faint"
            title="Set per connection in Admin → Connections"
          >
            cap {selected.limits.maxRows.toLocaleString()} ·{' '}
            {Math.round(selected.limits.timeoutMs / 1000)}s
          </span>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="min-h-0 flex-1">
        <SplitPane
          direction="horizontal"
          initial={260}
          min={180}
          max={520}
          storageKey="idas-sidebar-width"
          first={
            <div className="flex h-full flex-col">
              {/* Schema and saved queries are the two things people reach into
                  the sidebar for, so they share it rather than one of them
                  living on a page you have to navigate away to. */}
              <div className="flex shrink-0 gap-0.5 border-b border-edge bg-surface-raised px-1 py-1">
                {(['schema', 'saved'] as const).map((tab) => (
                  <button
                    key={tab}
                    className={clsx(
                      'flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-2xs font-medium transition-colors',
                      sidebarTab === tab
                        ? 'bg-surface-sunken text-ink'
                        : 'text-ink-muted hover:text-ink',
                    )}
                    onClick={() => setSidebarTab(tab)}
                  >
                    {tab === 'schema' ? (
                      <>
                        <Table2 className="h-3 w-3" /> Schema
                      </>
                    ) : (
                      <>
                        <Bookmark className="h-3 w-3" /> Saved
                      </>
                    )}
                  </button>
                ))}
              </div>

              <div className="min-h-0 flex-1">
                {sidebarTab === 'schema' ? (
                  <SchemaTree
                    snapshot={catalog.data}
                    loading={catalog.isLoading}
                    refreshing={refresh.isPending || Boolean(catalog.data?.refreshing)}
                    onRefresh={() => refresh.mutate()}
                    onInsert={(text) => editorRef.current?.insertAtCursor(text)}
                    onQuickRun={quickRun}
                    activeDatabase={activeTab.database}
                    onSelectDatabase={setDatabase}
                  />
                ) : (
                  <SavedQueriesPanel
                    user={user}
                    dataSourceId={dataSourceId}
                    onOpen={(q) => openSaved(q, false)}
                    onRun={(q) => openSaved(q, true)}
                  />
                )}
              </div>
            </div>
          }
          second={
            <div className="flex h-full flex-col border-l border-edge">
              <TabStrip
                tabs={tabs}
                activeTabId={activeTabId}
                onSelect={setActiveTab}
                onClose={closeTab}
                onAdd={() => addTab()}
              />

              <div className="min-h-0 flex-1">
                <SplitPane
                  direction="vertical"
                  initial={280}
                  min={120}
                  max={700}
                  storageKey="idas-editor-height"
                  first={
                    <SqlEditor
                      ref={editorRef}
                      value={activeTab.sql}
                      onChange={(sql) => updateTab(activeTab.id, { sql })}
                      onRun={() => void run()}
                      catalog={completions.data ?? null}
                      validation={activeTab.validation}
                      theme={theme}
                    />
                  }
                  second={
                    <ResultPanel
                      result={activeTab.result}
                      running={activeTab.running}
                      error={activeTab.error}
                      canExport={canExport}
                      onExport={() =>
                        activeTab.result && setExporting(activeTab.result.queryRunId)
                      }
                      onCancel={() => cancel.mutate()}
                      canCancel={Boolean(activeTab.clientQueryId) && !cancel.isPending}
                    />
                  }
                />
              </div>
            </div>
          }
        />
      </div>

      {exporting && (
        <ExportDialog
          queryRunId={exporting}
          rowCount={activeTab.result?.rowCount ?? 0}
          onClose={() => setExporting(null)}
        />
      )}

      {saving && dataSourceId && (
        <SavedQueryModal
          draftSql={activeTab.sql}
          dataSourceId={dataSourceId}
          onClose={() => setSaving(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
            setSidebarTab('saved');
            setSaving(false);
          }}
        />
      )}
    </div>
  );
}

function TabStrip({
  tabs,
  activeTabId,
  onSelect,
  onClose,
  onAdd,
}: {
  tabs: Array<{ id: string; title: string; running: boolean; validation: unknown }>;
  activeTabId: string;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onAdd: () => void;
}) {
  return (
    <div className="flex h-8 shrink-0 items-center gap-px overflow-x-auto border-b border-edge bg-surface-sunken px-1">
      {tabs.map((tab) => (
        <div
          key={tab.id}
          className={clsx(
            'group flex h-6 shrink-0 items-center gap-1.5 rounded px-2 text-2xs transition-colors',
            tab.id === activeTabId
              ? 'bg-surface text-ink'
              : 'text-ink-muted hover:bg-surface/50 hover:text-ink',
          )}
        >
          <button onClick={() => onSelect(tab.id)} className="max-w-32 truncate">
            {tab.title}
          </button>
          {tab.running && <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent" />}
          {tabs.length > 1 && (
            <button
              className="shrink-0 rounded opacity-0 transition-opacity hover:text-danger group-hover:opacity-100"
              onClick={() => onClose(tab.id)}
              aria-label={`Close ${tab.title}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          )}
        </div>
      ))}

      <button className="btn btn-ghost h-6 shrink-0 px-1" onClick={onAdd} aria-label="New query tab">
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}
